import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocation } from 'react-router'
import { useDispatch } from 'react-redux'
import { TabProvider } from '@tecuity/granite-ui/TabProvider'
import { TabButton } from '@tecuity/granite-ui/TabButton'
import { TabPanel } from '@tecuity/granite-ui/TabPanel'
import { Body } from 'components/Shell/Shell'
import { Spinner } from 'components/Loaders/Loaders'
import { SUBROUTES } from '../../../App'
import { FilingSourceTypeIds } from '../../../Utilities/Utilities'
import { getEntityNotes, saveNewNote, editNote, deleteNote } from '../RecordDetailsActions'
import { getMiscRecordDetails, getMiscRecordFilingHistory, getMiscRecordDetailConfig } from '../../../requests'
import MiscGenericDetails from './MiscGenericDetails'
import { buildMiscHistoryTemplate, buildMiscHistoryAmendments } from './miscHistoryUtils'
import NotesPanel from '../NotesPanel'
import RecordHistory from 'components/RecordHistory/RecordHistory'
import RecordDetailPageBackButton from '../RecordDetailPageBackButton'

const MiscRecordDetails = () => {
  const location = useLocation()
  const dispatch = useDispatch()

  const recordId = useMemo(() => {
    const parts = location.pathname.split('/')
    return parts[parts.length - 1]
  }, [location.pathname])

  const moduleId = useMemo(() => {
    return new URLSearchParams(location.search).get('moduleId')
  }, [location.search])

  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)
  const [recordData, setRecordData] = useState(null)
  const [moduleConfig, setModuleConfig] = useState(null)
  const [filingHistory, setFilingHistory] = useState(null)
  const [activeTabId, setActiveTabId] = useState('tab-history')

  useEffect(() => {
    let detailsDone = false
    let configDone = false
    let historyDone = false

    const checkAllDone = () => {
      if (detailsDone && configDone && historyDone) {
        setLoading(false)
      }
    }

    getMiscRecordDetails(
      recordId,
      (data) => {
        setRecordData(data)
        dispatch(getEntityNotes(FilingSourceTypeIds.Miscellaneous, data.MiscellaneousRecordId))
        detailsDone = true
        checkAllDone()
      },
      () => {
        setFetchError(true)
        detailsDone = true
        checkAllDone()
      }
    )

    getMiscRecordDetailConfig(
      moduleId,
      (data) => {
        setModuleConfig(data)
        if (!data.HistoryConfig?.IsEnabled) {
          setActiveTabId('tab-notes')
        }
        configDone = true
        checkAllDone()
      },
      () => {
        setFetchError(true)
        configDone = true
        checkAllDone()
      }
    )

    getMiscRecordFilingHistory(
      recordId,
      (data) => {
        setFilingHistory(data)
        historyDone = true
        checkAllDone()
      },
      () => {
        setFetchError(true)
        historyDone = true
        checkAllDone()
      }
    )
  }, [recordId, moduleId])

  const moduleName = useMemo(() => {
    return (
      SUBROUTES.flatMap((r) => r.MiscRecordsModules || []).find(
        (m) => String(m.MiscellaneousModuleId) === String(moduleId)
      )?.DisplayName ?? ''
    )
  }, [moduleId])

  const historyTemplate = useMemo(
    () => buildMiscHistoryTemplate(moduleConfig && moduleConfig.HistoryConfig),
    [moduleConfig]
  )

  const historyObject = useMemo(
    () => ({
      amendments: buildMiscHistoryAmendments(filingHistory),
      history: [],
      template: historyTemplate,
    }),
    [filingHistory, historyTemplate]
  )

  const saveNote = useCallback(
    (text) => dispatch(saveNewNote(text, FilingSourceTypeIds.Miscellaneous, recordData.MiscellaneousRecordId)),
    [dispatch, recordData]
  )

  const editNoteCallback = useCallback(
    (note) => dispatch(editNote(note, FilingSourceTypeIds.Miscellaneous, recordData.MiscellaneousRecordId)),
    [dispatch, recordData]
  )

  const deleteNoteCallback = useCallback(
    (noteId) => dispatch(deleteNote(noteId, FilingSourceTypeIds.Miscellaneous, recordData.MiscellaneousRecordId)),
    [dispatch, recordData]
  )

  if (loading) {
    return (
      <Body>
        <Spinner className="page-loader" themeColor="dark-neutral" />
      </Body>
    )
  }

  if (fetchError) {
    return (
      <Body>
        <p className="error-message">An error occurred loading the record. Please try again.</p>
      </Body>
    )
  }

  const historyEnabled = moduleConfig?.HistoryConfig?.IsEnabled

  return (
    <Body>
      <TabProvider activeTabId={activeTabId} onChange={setActiveTabId}>
        <div className="row header-row">
          <h1>Record Details</h1>
          <div className="tablist" role="tablist">
            {historyEnabled && (
              <TabButton id="tab-history" forPanelId="panel-history" label="History" />
            )}
            <TabButton id="tab-notes" forPanelId="panel-notes" label="Notes" />
          </div>
        </div>

        <MiscGenericDetails
          cards={moduleConfig ? moduleConfig.Cards : []}
          recordData={recordData}
          moduleName={moduleName}
        />

        {historyEnabled && (
          <TabPanel tabId="tab-history" id="panel-history">
            <RecordHistory
              history={historyObject}
              showChangedFromTo={moduleConfig?.HistoryConfig?.ShowChangedFromTo ?? true}
              requiredVoidRight="MiscellaneousVoid"
              module="misc"
            />
          </TabPanel>
        )}

        <TabPanel tabId="tab-notes" id="panel-notes">
          <NotesPanel
            saveNote={saveNote}
            editNote={editNoteCallback}
            deleteNote={deleteNoteCallback}
          />
        </TabPanel>
      </TabProvider>

      <RecordDetailPageBackButton />
    </Body>
  )
}

export default MiscRecordDetails
